import type { ReactNode } from 'react';
import { StyleSheet } from 'react-native';
import {
  Colors,
  Flex,
  IconMore,
  IconTrash,
  IconWrite,
  Typography,
} from '@banhala/ui-kit';
import {
  Menu,
  MenuOption,
  MenuOptions,
  MenuTrigger,
} from 'react-native-popup-menu';
import { isIOS } from 'shared/src/utils';
import { systemAlert } from 'shared/src/component/systemAlert';
import type { LikeFolder } from 'shared/src/api/likedFolderService/getLikedFolders/types';

interface FolderMoreMenuProps {
  folder: LikeFolder;
  onPressRename: (folder: LikeFolder) => void;
  onPressDelete: (folder: LikeFolder) => void;
}

interface MenuItem {
  label: string;
  color: 'content_primary' | 'content_negative';
  icon: ReactNode;
  destructive?: boolean;
  onPress: () => void;
}

// iOS: systemAlert / Android·Web: 컨텍스트 메뉴
export const FolderMoreMenu = ({
  folder,
  onPressRename,
  onPressDelete,
}: FolderMoreMenuProps) => {
  const items: MenuItem[] = [
    {
      label: '서랍 이름 바꾸기',
      color: 'content_primary',
      icon: <IconWrite size={24} fill={Colors.content_primary} />,
      onPress: () => onPressRename(folder),
    },
    {
      label: '서랍 삭제',
      color: 'content_negative',
      icon: <IconTrash size={24} fill={Colors.content_negative} />,
      destructive: true,
      onPress: () => onPressDelete(folder),
    },
  ];

  if (isIOS) {
    return (
      <MoreButton
        onPress={() =>
          systemAlert('', undefined, [
            ...items.map((item) => ({
              text: item.label,
              style: item.destructive ? ('destructive' as const) : undefined,
              onPress: item.onPress,
            })),
            { text: '취소', style: 'cancel' },
          ])
        }
      />
    );
  }

  return (
    // 카드 onPress(폴더 열기) 터치 전파 차단용
    <Flex onPress={() => undefined}>
      <Menu>
        <MenuTrigger>
          <MoreButton />
        </MenuTrigger>
        <MenuOptions customStyles={{ optionsContainer: styles.optionsContainer }}>
          {items.map((item) => (
            <MenuOption
              key={item.label}
              onSelect={item.onPress}
              customStyles={{ optionWrapper: styles.optionWrapper }}
            >
              <Flex.Row
                padding={{ horizontal: 16, vertical: 14 }}
                alignItems={'center'}
                justifyContent={'space-between'}
                gap={12}
              >
                <Typography type={'body2'} color={item.color} numberOfLines={1}>
                  {item.label}
                </Typography>
                {item.icon}
              </Flex.Row>
            </MenuOption>
          ))}
        </MenuOptions>
      </Menu>
    </Flex>
  );
};

const MoreButton = ({ onPress }: { onPress?: () => void }) => (
  <Flex
    padding={{ left: 12, vertical: 6 }}
    onPress={onPress}
    testID={`서랍더보기버튼`}
  >
    <IconMore size={16} fill={Colors.content_tertiary} />
  </Flex>
);

const styles = StyleSheet.create({
  optionsContainer: {
    borderRadius: 12,
    paddingVertical: 4,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 8,
  },
  optionWrapper: {
    padding: 0,
  },
});
